import React, { useState, ReactNode, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import 'primeicons/primeicons.css';
import './admin.css';
import { useAuth } from '../auth/hooks/useAuth';
import { getUserRole } from '../auth/service/storage.service';
import { UserRoles } from '../common/utils/util';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  allowedRoles?: string[];
}

interface AdminLayoutProps {
  children: ReactNode;
}

const navItems: NavItem[] = [
  {
    path: '/order',
    label: 'Pedidos pendientes',
    icon: 'pi pi-shopping-cart',
    allowedRoles: [UserRoles.ADMIN, UserRoles.VIEWER]
  },
  {
    path: '/deliveries',
    label: 'Guías',
    icon: 'pi pi-truck',
    allowedRoles: [UserRoles.ADMIN, UserRoles.VIEWER, UserRoles.EDITOR]
  },
  {
    path: '/users',
    label: 'Usuarios',
    icon: 'pi pi-users',
    allowedRoles: [UserRoles.ADMIN]
  }
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  // Obtener el estado inicial del drawer desde localStorage
  const getInitialDrawerState = (): boolean => {
    const saved = localStorage.getItem('drawerOpen');
    return saved !== null ? JSON.parse(saved) : true; // Por defecto abierto
  };

  const [drawerOpen, setDrawerOpen] = useState(getInitialDrawerState);
  const [filteredNavItems, setFilteredNavItems] = useState<NavItem[]>([]);
  const location = useLocation();
  const { logout } = useAuth();
  
  useEffect(() => {
    // Obtener el rol del usuario
    const role = getUserRole();
    
    // Filtrar elementos de navegación según el rol del usuario
    const filtered = navItems.filter(item => {
      // Si no hay roles específicos, el ítem es accesible para todos los usuarios autenticados
      if (!item.allowedRoles || item.allowedRoles.length === 0) {
        return true;
      }
      // Si hay roles específicos, verificar si el usuario tiene uno de esos roles
      return role ? item.allowedRoles.includes(role) : false;
    });
    
    setFilteredNavItems(filtered);
  }, []);

  const toggleDrawer = () => {
    const newState = !drawerOpen;
    setDrawerOpen(newState);
    // Guardar el estado en localStorage
    localStorage.setItem('drawerOpen', JSON.stringify(newState));
  };

  const handleLogout = () => {
   logout();
  };

  const currentNavItem = filteredNavItems.find(item => 
    location.pathname.startsWith(item.path)
  );
  
  const pageTitle = currentNavItem?.label || '';

  return (
    <div className="admin-layout">
      {/* Drawer */}
      <div className={`drawer ${drawerOpen ? 'open' : 'closed'}`}>
        <div className="drawer-header">
          {drawerOpen ? <h3>Bicicletas Mercurio</h3> : <div className="compact-logo">BM</div>}
          <button onClick={toggleDrawer} className="toggle-button">
            <i className={`pi ${drawerOpen ? 'pi-chevron-left' : 'pi-chevron-right'}`}></i>
          </button>
        </div>
        <nav className="drawer-nav">
          <ul>
            {filteredNavItems.map((item) => (
              <li key={item.path}>
                <Link 
                  to={item.path} 
                  className={location.pathname.startsWith(item.path) ? 'active' : ''}
                  title={item.label}
                >
                  <i className={item.icon}></i>
                  {drawerOpen && <span className="nav-label">{item.label}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="drawer-footer">
          <button onClick={handleLogout} className="logout-button" title="Cerrar Sesión">
            <i className="pi pi-sign-out"></i>
            {drawerOpen && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      <div className={`main-admin ${drawerOpen ? 'with-drawer' : 'full-width'}`}>
        <header className="content-header">
          <h1>{pageTitle}</h1>
        </header>
        <main>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 